import {Button, Tag} from 'antd';
import {MenuOutlined} from '@ant-design/icons';
import PropTypes from 'prop-types';
import {useState} from 'react';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import {arrayMoveImmutable} from 'array-move';

import styles from './createWalletCurrency.module.scss';
import PaymentMethodConfigModal from './PaymentMethodConfigModal';
import {
  applyMethodOrder,
  emptyConfigForm,
  hasMethodOverrides,
  methodLabel,
  orderMethods,
} from './paymentMethodConfig';

// Same drag primitives as the quick actions list, so reordering behaves the
// same way in both places.
const DragHandle = SortableHandle(({className}) => (
  <span className={className}>
    <MenuOutlined />
  </span>
));

const SortableRow = SortableElement(props => <li {...props} />);
const SortableList = SortableContainer(props => <ul {...props} />);

/**
 * Per-method configuration for one direction of a currency.
 *
 * A row per method showing its current state, with the editing itself in a
 * modal. The fields were originally inline in an accordion, which put eight
 * tall panels of mostly-empty overrides between the deposit limits and the
 * withdrawal section — most methods keep the app's own wording, so the common
 * case is a list to skim rather than a form to fill.
 *
 * Rows come from `enabledPaymentMethods`, not from the stored config array: a
 * method the currency accepts but has never been configured still needs a row,
 * and a method dropped from that list should stop being editable. Deposit and
 * withdrawal get their own editor because they are separate configs for the
 * same method — WIRE_TRANSFER reads "2 - 4 Business days" on deposit and
 * "1 - 5 Business days" on withdrawal.
 *
 * `value` is the whole keyed config object for this direction, injected by the
 * wrapping <Form.Item>; the form store stays the single source of truth.
 */
const PaymentMethodConfigEditor = ({
  methods = [],
  value,
  onChange,
  countries = [],
  directionLabel,
}) => {
  const state = value || {};
  const [editing, setEditing] = useState(null);

  // Position in this list *is* the order, which is what makes a duplicate
  // impossible; the old number box happily took 1 on two different rows.
  const rows = orderMethods(methods, state);

  if (!rows.length) return null;

  const handleSave = draft => {
    onChange({...state, [editing]: draft});
    setEditing(null);
  };

  const handleSortEnd = ({oldIndex, newIndex}) => {
    if (oldIndex === newIndex) return;
    onChange(
      applyMethodOrder(arrayMoveImmutable(rows, oldIndex, newIndex), state),
    );
  };

  return (
    <div className={styles.configEditor}>
      <div className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>
          How each {directionLabel.toLowerCase()} method looks
        </h2>
        <p className={styles.cardHint}>
          Drag to set the order users see. Everything else overrides what the
          app shows — anything left alone keeps the app’s own wording.
        </p>
      </div>

      <SortableList
        useDragHandle
        lockAxis="y"
        helperClass={styles.methodRowDragging}
        onSortEnd={handleSortEnd}
        className={styles.methodList}>
        {rows.map((method, index) => {
          const form = {...emptyConfigForm(), ...(state[method] || {})};
          const restricted = (form.restrictedCountries || []).length;

          return (
            <SortableRow
              key={method}
              index={index}
              className={styles.methodRow}>
              <div className={styles.methodInfo}>
                <DragHandle className={styles.methodDrag} />
                <span className={styles.methodPosition}>{index + 1}</span>
                {form.logoUrl ? (
                  <img
                    className={styles.methodLogo}
                    src={form.logoUrl}
                    alt=""
                  />
                ) : (
                  <span className={styles.methodLogoBlank} />
                )}
                <span className={styles.methodName}>{methodLabel(method)}</span>
                {form.enabled === false ? (
                  <Tag color="orange">Hidden</Tag>
                ) : form.available === false ? (
                  <Tag color="volcano">Unavailable</Tag>
                ) : hasMethodOverrides(form) ? (
                  <Tag color="purple">Configured</Tag>
                ) : (
                  <Tag>App defaults</Tag>
                )}
                {restricted > 0 && (
                  <Tag color="red">{restricted} country restriction(s)</Tag>
                )}
              </div>

              <Button size="small" onClick={() => setEditing(method)}>
                {hasMethodOverrides(form) ? 'Edit' : 'Configure'}
              </Button>
            </SortableRow>
          );
        })}
      </SortableList>

      <PaymentMethodConfigModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        onSave={handleSave}
        method={editing}
        methodName={editing ? methodLabel(editing) : ''}
        directionLabel={directionLabel}
        config={editing ? state[editing] : undefined}
        countries={countries}
      />
    </div>
  );
};

PaymentMethodConfigEditor.propTypes = {
  methods: PropTypes.array,
  value: PropTypes.object,
  onChange: PropTypes.func,
  countries: PropTypes.array,
  directionLabel: PropTypes.string,
};

export default PaymentMethodConfigEditor;
